import React, { useState } from 'react'
import dateFormat from 'date-fns/format'

import { Button, Typography } from '../../components/Wrappers'
import { useApiHttpState } from '../../context/ApiContext'
import { useAddSnackbarQueue } from '../../context/SnackbarContext'
import { cancelAppointment } from '../../apis'
import useStyles from './styles'
import {
    formatAppointmentTimeRange,
    formatAppointmentTypeName,
    formatDuration,
    formatHandlerName,
    formatHouseholdName,
    toLocalDate,
} from './calendarHelpers'

/**
 * CancelConfirmation - the confirmation step in front of cancelling an
 * appointment (Requirement 13.2), rendered as the shared dialog's content the
 * same way pages/knowledgeBase/DeleteConfirmation.js is:
 *
 *   openDialog('Cancel Appointment',
 *       <CancelConfirmation appointment={a} onCancelled={fn} close={closeDialog} />)
 *
 * The dialog chrome (title, Escape / X to dismiss, focus trap, the Close button
 * that ends up autofocused) comes from context/components/Dialog.js, so the
 * DESTRUCTIVE button lives here and nothing in here can dismiss the dialog by
 * accident - walking away is the default.
 *
 * IT OWNS THE MUTATION, like DeleteConfirmation does: the PUT is fired here, a
 * failure is shown HERE with the dialog still open (the caller has already been
 * told nothing, so there is nothing to undo), and only a success closes and
 * reports back through `onCancelled`.
 *
 * PROPS
 *  appointment  object  The row being cancelled (as GET /appointments/:id
 *                       returns it - the household, handler and appointmentType
 *                       relations are what the summary reads).
 *  onCancelled(appointment)  Called ONLY after the api confirmed the
 *                       cancellation, with the updated row. The caller
 *                       revalidates its own data with it.
 *  close()      fn      Closes the dialog (useCloseDialog).
 *
 * WHY NOT useOpenConfirmationDialog: its DialogConfirmContent offers one
 * primary-coloured "Submit" button, cannot show a failed request, and cannot say
 * WHICH appointment is about to be cancelled. An irreversible action (the UI has
 * no un-cancel) needs all three.
 */
const CancelConfirmation = ({ appointment, onCancelled, close }) => {
    const classes = useStyles()
    const { api } = useApiHttpState()
    const addSnackbarQueue = useAddSnackbarQueue()

    //local, not the shared ApiContext `loading`: only THIS request should put
    //this button in its pending state
    const [pending, setPending] = useState(false)
    const [error, setError] = useState(null)

    const appointmentId = appointment ? appointment.id : null

    // toLocalDate, never new Date(row.date): `date` is a mysql DATE that arrives
    // as midnight UTC, so parsing it as a timestamp renders the previous day in
    // every US timezone (see the header of calendarHelpers.js).
    const localDate = toLocalDate(appointment && appointment.date)

    //spelled out as label/value lines rather than a compressed sentence, so the
    //user can check they are cancelling the right appointment
    const summary = [
        { label: 'Date', value: localDate ? dateFormat(localDate, 'EEEE, MMMM d, yyyy') : '' },
        { label: 'Time', value: formatAppointmentTimeRange(appointment) },
        { label: 'Duration', value: formatDuration(appointment && appointment.durationMinutes) },
        { label: 'Type', value: formatAppointmentTypeName(appointment) },
        { label: 'Handler', value: formatHandlerName(appointment && appointment.handler) },
        {
            label: 'Household',
            value:
                formatHouseholdName(appointment && appointment.household) ||
                (appointment && appointment.householdId ? `Household #${appointment.householdId}` : ''),
        },
    ].filter(row => !!row.value)

    const handleSubmit = async () => {
        if (!appointmentId || pending) return
        setError(null)
        setPending(true)
        const [er, response] = await cancelAppointment(api, appointmentId)
        setPending(false)

        if (er) {
            //every appointments error body is {status:'failed',message} - er.message
            //would read "Request failed with status code 400"
            const data = er.response && er.response.data && typeof er.response.data === 'object' ? er.response.data : null
            setError((data && data.message) || 'The appointment could not be cancelled. Please try again.')
            return
        }

        addSnackbarQueue('Appointment was cancelled', 'success')
        close()
        //after the dialog is out of the way, so the caller is free to close the
        //panel this was opened from
        if (onCancelled) onCancelled(response ? response.data : null)
    }

    if (!appointmentId)
        return (
            <div role="alert">
                <Typography color="secondary">This appointment can no longer be cancelled from here.</Typography>
            </div>
        )

    return (
        <div className={classes.cancelConfirmation}>
            <Typography color="text">Cancel this appointment?</Typography>

            <div className={classes.cancelConfirmationSummary}>
                {summary.map(row => (
                    <Typography key={row.label} color="text" size="sm">
                        {`${row.label}: ${row.value}`}
                    </Typography>
                ))}
            </div>

            {/* what cancelling actually does, said BEFORE it happens: the record is
                kept (Requirement 13.2's soft delete), the household hears about it
                (Requirement 13.3), and it leaves the appointments calendar because
                that endpoint excludes cancelled rows */}
            <Typography color="text" size="sm" colorBrightness="secondary">
                The appointment is kept on record and marked as cancelled, and it stops being counted against the
                handler's availability. It is removed from the appointments calendar and stays visible, as cancelled, on
                the household's page. This cannot be undone here.
            </Typography>
            <Typography color="text" size="sm" colorBrightness="secondary">
                The household is notified by SMS if a phone number is on record.
            </Typography>

            {/* a failed cancellation is said out loud with the dialog still open, so
                the user can retry instead of wondering whether it went through */}
            {!!error && (
                <div className={classes.cancelConfirmationError} role="alert">
                    <Typography variant="body2" color="secondary">
                        {error}
                    </Typography>
                </div>
            )}

            <div className={classes.cancelConfirmationActions}>
                <Button
                    variant="contained"
                    size="small"
                    color="error"
                    pending={pending}
                    onClick={handleSubmit}
                    className={classes.margin}
                >
                    Cancel Appointment
                </Button>
            </div>
            <Typography variant="caption" color="text" colorBrightness="secondary">
                Use Close, Escape or the X above to keep the appointment.
            </Typography>
        </div>
    )
}

export default CancelConfirmation
